/**
 * Guardian Progress Report - AI-generated progress summary for parents
 *
 * Displays a parent-friendly summary of their linked athlete's progress,
 * including what they're doing well and how to support them.
 */

import { useState, useEffect } from 'react';
import { getProgressReport, type ProgressReport } from '@tss/shared';
import {
  FileText,
  Sparkles,
  ThumbsUp,
  Heart,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface GuardianProgressReportProps {
  athleteId?: string; // Optional - if not provided, gets report for all linked athletes
}

export function GuardianProgressReport({ athleteId }: GuardianProgressReportProps) {
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadReport();
  }, [athleteId]);

  async function loadReport() {
    try {
      setError(null);
      const data = await getProgressReport(athleteId);
      setReport(data);
    } catch (err) {
      console.error('Failed to load progress report:', err);
      setError('Unable to load report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadReport();
  }

  if (loading) {
    return (
      <div
        className="card"
        style={{
          padding: '24px',
          background:
            'linear-gradient(135deg, rgba(var(--green-rgb), 0.06) 0%, rgba(var(--green-rgb), 0.02) 100%)',
          border: '1px solid rgba(var(--green-rgb), 0.15)',
        }}
      >
        <div className="flex-center" style={{ padding: '20px' }}>
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--color-success)' }} />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return null; // Silently fail
  }

  // Don't show if there's no meaningful content
  if (
    !report.report ||
    report.report.includes('not linked') ||
    report.report.includes('Join an organization')
  ) {
    return null;
  }

  return (
    <div
      className="card"
      style={{
        padding: '20px',
        background:
          'linear-gradient(135deg, rgba(var(--green-rgb), 0.06) 0%, rgba(var(--green-rgb), 0.02) 100%)',
        border: '1px solid rgba(var(--green-rgb), 0.15)',
        marginBottom: '24px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(var(--green-rgb), 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FileText size={20} style={{ color: 'var(--color-success)' }} />
          </div>
          <div>
            <h3
              style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-900)', margin: 0 }}
            >
              Progress Report
            </h3>
            <p
              style={{
                fontSize: '12px',
                color: 'var(--gray-500)',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Sparkles size={10} />
              AI-powered summary
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-ghost"
            style={{
              padding: '6px',
              borderRadius: '50%',
              opacity: refreshing ? 0.5 : 1,
            }}
            title="Refresh report"
          >
            <RefreshCw
              size={16}
              className={refreshing ? 'animate-spin' : ''}
              style={{ color: 'var(--gray-400)' }}
            />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="btn-ghost"
            style={{ padding: '6px', borderRadius: '50%' }}
          >
            {expanded ? (
              <ChevronUp size={16} style={{ color: 'var(--gray-400)' }} />
            ) : (
              <ChevronDown size={16} style={{ color: 'var(--gray-400)' }} />
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Main Report */}
          <div
            style={{
              padding: '16px',
              background: 'white',
              borderRadius: 'var(--radius-md)',
              marginBottom: '16px',
            }}
          >
            <p style={{ fontSize: '14px', color: 'var(--gray-700)', lineHeight: '1.7', margin: 0 }}>
              {report.report}
            </p>
          </div>

          {/* Athlete Cards */}
          {report.athletes && report.athletes.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {report.athletes.map((athlete) => (
                <div
                  key={athlete.athlete_id}
                  style={{
                    padding: '14px',
                    background: 'white',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--gray-100)',
                  }}
                >
                  {/* Athlete Name */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '12px',
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '7px',
                        background:
                          'linear-gradient(135deg, var(--color-success) 0%, #16a34a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        flexShrink: 0,
                      }}
                    >
                      {athlete.name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--gray-900)',
                          margin: 0,
                        }}
                      >
                        {athlete.name}
                      </p>
                      {athlete.video_count !== undefined && (
                        <p style={{ fontSize: '11px', color: 'var(--gray-500)', margin: 0 }}>
                          {athlete.video_count} training video{athlete.video_count !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* AI Summary */}
                  {athlete.ai_summary && (
                    <p
                      style={{
                        fontSize: '13px',
                        color: 'var(--gray-600)',
                        marginBottom: '10px',
                        lineHeight: '1.5',
                      }}
                    >
                      {athlete.ai_summary}
                    </p>
                  )}

                  {/* Doing Well & Support Tip */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {athlete.doing_well && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <ThumbsUp
                          size={14}
                          style={{ color: 'var(--color-success)', marginTop: '2px', flexShrink: 0 }}
                        />
                        <p style={{ fontSize: '12px', color: 'var(--gray-600)', margin: 0 }}>
                          <strong style={{ color: 'var(--color-success)' }}>Doing well:</strong>{' '}
                          {athlete.doing_well}
                        </p>
                      </div>
                    )}
                    {athlete.support_tip && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <Heart
                          size={14}
                          style={{ color: '#ec4899', marginTop: '2px', flexShrink: 0 }}
                        />
                        <p style={{ fontSize: '12px', color: 'var(--gray-600)', margin: 0 }}>
                          <strong style={{ color: '#ec4899' }}>How to help:</strong>{' '}
                          {athlete.support_tip}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
